import { Shield, Lock, Settings, Database, FileText } from 'lucide-react';

import Docker from '@/assets/ico/vendor/docker.svg?c';
import Kubernetes from '@/assets/ico/vendor/kubernetes.svg?c';
import type { Environment } from '@/react/portainer/environments/types';
import type { EnvironmentGroup } from '@/react/portainer/environments/environment-groups/types';

import { BoxSelector, BoxSelectorOption } from '@@/BoxSelector';
import { Badge } from '@@/Badge';
import { FormSectionTitle } from '@@/form-components/FormSectionTitle';
import { Link } from '@@/Link';

import {
  PolicyType,
  PolicyCategory,
  EnvironmentTypeFilter,
  CategoryFilter,
  EmptyStateMessageType,
  PolicyTemplate,
} from '../../types';
import {
  getVisiblePolicyTypes,
  getCompatibleGroupsCount,
} from '../../utils/policyTypeUtils';
import {
  getAllPolicyTypesMetadata,
  PolicyTypeMetadata,
} from '../../utils/policy-types-metadata';
import { filterPolicyTypes } from '../../utils/filterPolicyTypes';
import { usePolicyTemplates } from '../../queries/usePolicyTemplates';

import { PolicyTypeToolbar } from './PolicyTypeToolbar';
import {
  getEmptyStateMessageType,
  filterTemplates,
  getAvailableEnvironmentTypes,
  getAvailableCategories,
  buildEnvironmentFilterOptions,
  buildCategoryFilterOptions,
} from './policyTypeSelectorUtils';

interface Props {
  value: PolicyType | null;
  onChange: (policyType: PolicyType) => void;
  onTemplateSelect?: (template: PolicyTemplate) => void;
  selectedTemplateId?: string | null;
  environments: Environment[];
  groups: EnvironmentGroup[];
  environmentFilter: EnvironmentTypeFilter;
  onEnvironmentFilterChange: (filter: EnvironmentTypeFilter) => void;
  categoryFilter: CategoryFilter;
  onCategoryFilterChange: (filter: CategoryFilter) => void;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
}

export function PolicyTypeSelector({
  value,
  onChange,
  onTemplateSelect,
  selectedTemplateId,
  environments,
  groups,
  environmentFilter,
  onEnvironmentFilterChange,
  categoryFilter,
  onCategoryFilterChange,
  searchQuery,
  onSearchQueryChange,
}: Props) {
  const compatiblePolicyTypes = getVisiblePolicyTypes(environments, groups);
  const allPolicyTypes = getAllPolicyTypesMetadata();
  const templatesQuery = usePolicyTemplates();
  const templates = templatesQuery.data || [];

  const isAllView = environmentFilter === 'all';

  // Default shows only compatible policy types.
  // Selecting the 'all' filter shows every policy type, even if it has 0 compatible groups.
  const basePolicyTypes =
    environmentFilter === 'all' ? allPolicyTypes : compatiblePolicyTypes;

  // Filter policy types based on selected environment type, category, and search
  const filteredPolicyTypes = filterPolicyTypes(
    basePolicyTypes,
    environmentFilter,
    categoryFilter,
    searchQuery
  );

  // Filter templates based on current filters
  const filteredTemplates = filterTemplates(
    templates,
    environmentFilter,
    categoryFilter,
    searchQuery
  );

  // Group by environment type (Kubernetes/Docker), then by policy type
  const environmentSections = groupByEnvironmentType(
    filteredPolicyTypes,
    filteredTemplates,
    environments,
    groups,
    isAllView
  );

  const hasNoPolicyTypes = environmentSections.length === 0;

  const availableEnvTypes = getAvailableEnvironmentTypes(basePolicyTypes);
  const availableCategories = getAvailableCategories(basePolicyTypes);

  const environmentFilterOptions =
    buildEnvironmentFilterOptions(availableEnvTypes);
  const categoryFilterOptions = buildCategoryFilterOptions(availableCategories);

  const selectedIsSupported =
    !!value && getCompatibleGroupsCount(value, environments, groups) > 0;

  const showUnsupportedNotice = !!value && !selectedIsSupported;

  return (
    <div>
      <FormSectionTitle>Policy type</FormSectionTitle>
      <PolicyTypeToolbar
        searchQuery={searchQuery}
        onSearchQueryChange={onSearchQueryChange}
        environmentFilter={environmentFilter}
        environmentFilterOptions={environmentFilterOptions}
        onEnvironmentFilterChange={onEnvironmentFilterChange}
        categoryFilter={categoryFilter}
        categoryFilterOptions={categoryFilterOptions}
        onCategoryFilterChange={onCategoryFilterChange}
      />
      {hasNoPolicyTypes ? (
        <EmptyState
          envTypeSelected={environmentFilter}
          hasPolicyTypesBeforeFilters={basePolicyTypes.length > 0}
          hasSearchOrCategoryFilter={!!searchQuery || categoryFilter !== 'all'}
        />
      ) : (
        <div>
          {environmentSections.map((envSection) => (
            <EnvironmentSection
              key={envSection.environmentType}
              section={envSection}
              selectedValue={
                selectedTemplateId
                  ? `template:${selectedTemplateId}`
                  : value || ''
              }
              onSelect={(selectedValue) => {
                // Check if the selected value is a template
                if (selectedValue.startsWith('template:')) {
                  const templateId = selectedValue.replace('template:', '');
                  const template = templates.find((t) => t.id === templateId);
                  if (template) {
                    onChange(template.type);
                    onTemplateSelect?.(template);
                  }
                } else {
                  onChange(selectedValue as PolicyType);
                }
              }}
            />
          ))}

          {showUnsupportedNotice && (
            <div className="mt-2 text-sm text-warning-7">
              This policy type requires a compatible environment group to
              continue.{' '}
              <Link to="portainer.groups" data-cy="environment-groups-link">
                Add an environment group
              </Link>{' '}
              or pick a different policy type.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Compact description for cards within policy type rows
 * Simpler than OptionDescription - no environment badges since context is clear
 */
function CompactOptionDescription({
  description,
  compatibleGroupsCount,
  isTemplate = false,
}: {
  description: string;
  compatibleGroupsCount: number;
  isTemplate?: boolean;
}) {
  return (
    <div className="flex flex-col gap-2">
      {/* Template badge */}
      {isTemplate && (
        <Badge type="info" className="w-fit">
          <FileText className="lucide-icon mr-1 !h-3 !w-3" />
          Template
        </Badge>
      )}

      {/* Description */}
      <div className="text-xs text-muted">{description}</div>

      {/* Groups count */}
      <div className="text-xs text-muted">
        {compatibleGroupsCount} environment{' '}
        {compatibleGroupsCount === 1 ? 'group' : 'groups'}
      </div>
    </div>
  );
}

/**
 * Get the unsupported tooltip if applicable
 */
function getUnsupportedTooltip(isAllView: boolean, isSupported: boolean) {
  return isAllView && !isSupported
    ? () => 'No compatible environment groups available for this policy type'
    : undefined;
}

/**
 * Represents a row of options for a single policy type
 */
interface PolicyTypeRowData {
  policyType: PolicyType;
  displayName: string;
  category: PolicyCategory;
  options: Array<BoxSelectorOption<string>>;
}

/**
 * Represents a section grouped by environment type (Kubernetes/Docker)
 */
interface EnvironmentSectionData {
  environmentType: 'kubernetes' | 'docker';
  displayName: string;
  policyTypeRows: Array<PolicyTypeRowData>;
}

/**
 * Group by environment type (Kubernetes/Docker), then by policy type within each
 */
function groupByEnvironmentType(
  policyTypes: Array<PolicyTypeMetadata>,
  templates: Array<PolicyTemplate>,
  environments: Environment[],
  groups: EnvironmentGroup[],
  isAllView: boolean
): Array<EnvironmentSectionData> {
  // Separate policy types by environment
  const k8sPolicyTypes = policyTypes.filter((p) => p.id.endsWith('-k8s'));
  const dockerPolicyTypes = policyTypes.filter((p) => p.id.endsWith('-docker'));

  const sections: Array<EnvironmentSectionData> = [];

  // Build Kubernetes section
  if (k8sPolicyTypes.length > 0) {
    const k8sRows = buildPolicyTypeRows(
      k8sPolicyTypes,
      templates,
      environments,
      groups,
      isAllView
    );
    sections.push({
      environmentType: 'kubernetes',
      displayName: 'Kubernetes',
      policyTypeRows: k8sRows,
    });
  }

  // Build Docker section
  if (dockerPolicyTypes.length > 0) {
    const dockerRows = buildPolicyTypeRows(
      dockerPolicyTypes,
      templates,
      environments,
      groups,
      isAllView
    );
    sections.push({
      environmentType: 'docker',
      displayName: 'Docker',
      policyTypeRows: dockerRows,
    });
  }

  return sections;
}

/**
 * Build policy type rows for a given set of policy types
 */
function buildPolicyTypeRows(
  policyTypes: Array<PolicyTypeMetadata>,
  templates: Array<PolicyTemplate>,
  environments: Environment[],
  groups: EnvironmentGroup[],
  isAllView: boolean
): Array<PolicyTypeRowData> {
  return policyTypes.map((metadata) => {
    const templatesForType = templates.filter((t) => t.type === metadata.id);
    const options: Array<BoxSelectorOption<string>> = [];

    // Add templates first
    templatesForType.forEach((template) => {
      options.push(
        buildTemplateOption(template, environments, groups, isAllView)
      );
    });

    // Add custom "from scratch" option
    options.push(
      buildPolicyTypeOption(metadata, environments, groups, isAllView)
    );

    return {
      policyType: metadata.id,
      displayName: metadata.displayName,
      category: metadata.category,
      options,
    };
  });
}

/**
 * Renders a section for an environment type (Kubernetes/Docker)
 */
function EnvironmentSection({
  section,
  selectedValue,
  onSelect,
}: {
  section: EnvironmentSectionData;
  selectedValue: string;
  onSelect: (value: string) => void;
}) {
  const Icon = section.environmentType === 'kubernetes' ? Kubernetes : Docker;

  return (
    <div className="mb-6">
      <h3 className="flex items-center gap-2 text-base font-semibold mb-2 pb-2 border-b border-gray-5">
        <Icon className="w-7 h-7" />
        {section.displayName}
      </h3>
      <div className="space-y-3">
        {section.policyTypeRows.map((row) => (
          <PolicyTypeRow
            key={row.policyType}
            row={row}
            selectedValue={selectedValue}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Renders a row for a single policy type with visual distinction
 */
function PolicyTypeRow({
  row,
  selectedValue,
  onSelect,
}: {
  row: PolicyTypeRowData;
  selectedValue: string;
  onSelect: (value: string) => void;
}) {
  const Icon = getPolicyIcon(row.category);
  // Remove environment suffix from display name (e.g., "Kubernetes Security" → "Security")
  const shortName = getCategoryDisplayName(row.category);

  return (
    <div className="rounded-lg border border-solid border-gray-5 bg-gray-2 th-dark:bg-gray-iron-10 th-dark:border-gray-warm-9 p-3">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="lucide-icon !h-5 !w-5 text-gray-8 th-dark:text-gray-warm-4" />
        <h4 className="text-base font-semibold text-gray-8 th-dark:text-gray-warm-4 m-0">
          {shortName}
        </h4>
      </div>
      {/* Wrapper to make card headers smaller */}
      <div className="[&_.header]:!text-sm [&_.header]:!font-medium">
        <BoxSelector<string>
          radioName="policy-type"
          value={selectedValue}
          onChange={onSelect}
          options={row.options}
          useGridLayout
          className="-mb-3"
        />
      </div>
    </div>
  );
}

/**
 * Get short display name for a category (without environment prefix)
 */
function getCategoryDisplayName(category: PolicyCategory): string {
  switch (category) {
    case 'rbac':
      return 'RBAC';
    case 'security':
      return 'Security';
    case 'setup':
      return 'Setup';
    case 'registry':
      return 'Registry';
    default:
      return category;
  }
}

/**
 * Build a BoxSelectorOption for a policy type (custom from scratch)
 */
function buildPolicyTypeOption(
  metadata: PolicyTypeMetadata,
  environments: Environment[],
  groups: EnvironmentGroup[],
  isAllView: boolean
): BoxSelectorOption<PolicyType> {
  const compatibleGroupsCount = getCompatibleGroupsCount(
    metadata.id,
    environments,
    groups
  );
  const isSupported = compatibleGroupsCount > 0;

  return {
    id: metadata.id,
    value: metadata.id,
    label: 'Custom',
    icon: undefined as never, // Icon is shown in row header instead
    tooltip: getUnsupportedTooltip(isAllView, isSupported),
    description: (
      <CompactOptionDescription
        description={metadata.description}
        compatibleGroupsCount={compatibleGroupsCount}
      />
    ),
  };
}

/**
 * Display appropriate message when no policy types are available
 */
function EmptyState({
  envTypeSelected,
  hasPolicyTypesBeforeFilters,
  hasSearchOrCategoryFilter,
}: {
  envTypeSelected: EnvironmentTypeFilter;
  hasPolicyTypesBeforeFilters: boolean;
  hasSearchOrCategoryFilter: boolean;
}) {
  const messageType = getEmptyStateMessageType(
    envTypeSelected,
    hasPolicyTypesBeforeFilters,
    hasSearchOrCategoryFilter
  );

  const messages: Record<EmptyStateMessageType, React.ReactNode> = {
    filtered: 'No policy types match the current filters.',
    'all-environments': (
      <>
        No compatible environment groups available. Create{' '}
        <Link to="portainer.groups.new" data-cy="environments-view-link">
          environment groups
        </Link>{' '}
        with Docker, Podman, or Swarm agents, or Kubernetes edge agents to use
        this feature.
      </>
    ),
    kubernetes: (
      <>
        No compatible environment groups available. Create{' '}
        <Link to="portainer.groups.new" data-cy="environments-view-link">
          environment groups
        </Link>{' '}
        with Kubernetes edge agents to use this feature.
      </>
    ),
    docker: (
      <>
        No compatible environment groups available. Create{' '}
        <Link to="portainer.groups.new" data-cy="environments-view-link">
          environment groups
        </Link>{' '}
        with Docker, Podman, or Swarm agents to use this feature.
      </>
    ),
  };

  return <div className="text-sm text-muted">{messages[messageType]}</div>;
}

/**
 * Get icon for policy category
 */
function getPolicyIcon(category: PolicyCategory) {
  switch (category) {
    case 'rbac':
      return Lock;
    case 'security':
      return Shield;
    case 'setup':
      return Settings;
    case 'registry':
      return Database;
    default:
      return Shield;
  }
}

/**
 * Build a BoxSelectorOption for a policy template
 */
function buildTemplateOption(
  template: PolicyTemplate,
  environments: Environment[],
  groups: EnvironmentGroup[],
  isAllView: boolean
): BoxSelectorOption<string> {
  const compatibleGroupsCount = getCompatibleGroupsCount(
    template.type,
    environments,
    groups
  );
  const isSupported = compatibleGroupsCount > 0;

  return {
    id: `template:${template.id}`,
    value: `template:${template.id}`,
    label: template.name,
    icon: undefined as never, // Icon is shown in row header instead
    tooltip: getUnsupportedTooltip(isAllView, isSupported),
    description: (
      <CompactOptionDescription
        description={template.description}
        compatibleGroupsCount={compatibleGroupsCount}
        isTemplate
      />
    ),
  };
}
